import { EMAIL_VALIDATION_REGEX, HTTP_PROTOCOL, HTTPS_PROTOCOL } from "@config/constants";

export function addLeadingZero(a: string, b: number) {
  return (1e15 + a + "").slice(-b);
}

export function normalizeString(str: string) {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function removeHttpPrefix(url: string): string {
  return url.replace(/^https?:\/\//, "");
}

export function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
}

export function validateURL(url: string) {
  try {
    const urlObj = new URL(url);

    // Check if URL starts with http:// or https://
    if (!url.startsWith(HTTP_PROTOCOL) && !url.startsWith(HTTPS_PROTOCOL)) {
      return false;
    }

    // Check protocol
    if (!urlObj.protocol || !["http:", "https:"].includes(urlObj.protocol)) {
      return false;
    }

    // Check hostname - must be domain name or IP address with at least one dot
    const hostname = urlObj.hostname;
    if (!hostname.includes(".") || url.length > 200) {
      return false;
    }

    const splitHostname = hostname.split(".");
    const isValidDomain = splitHostname.every(
      (part) =>
        part.length > 0 &&
        part.length < 50 &&
        /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(part) &&
        !/\d/.test(splitHostname[splitHostname.length - 1]) // last item cannot contain numbers
    );

    const isValidIP =
      hostname.split(".").length === 4 &&
      hostname.split(".").every((num) => {
        const n = parseInt(num);
        return !isNaN(n) && n >= 0 && n <= 255;
      });

    return isValidDomain || isValidIP;
  } catch {
    return false;
  }
}

export function getDomainFromEmail(email: string): string {
  const domain = email.split("@")[1];
  return domain || "";
}

/**
 * Adds https protocol to a URL string if it doesn't already have one.
 * Attempts to parse the URL first to check if it's valid with a protocol.
 * If parsing fails, assumes no protocol and prepends https://.
 *
 * @param {string} url - The URL string to check/modify
 * @returns {string} URL with https:// protocol added if needed
 *
 * @example
 * addUrlProtocol('example.com') // Returns 'https://example.com'
 * addUrlProtocol('https://example.com') // Returns 'https://example.com'
 * addUrlProtocol('http://example.com') // Returns 'http://example.com'
 */
export function addUrlProtocol(url: string) {
  const trimmed = typeof url === "string" ? url.trim() : "";

  if (trimmed.length === 0) return "";

  try {
    new URL(url);
    return url; // string already has protocol
  } catch {
    return `https://${url.trim()}`;
  }
}

/**
 * Normalizes a URL by removing www. prefix from subdomains and trailing slashes from pathname,
 * while preserving protocol, search params and hash fragments.
 *
 * @param {string} url - The URL string to normalize. Can be with or without protocol.
 * @returns {string | null} The normalized URL string with:
 * - Protocol preserved or https:// added if missing
 * - www. removed only if it's a subdomain (e.g. www.example.com -> example.com but www.api.example.com unchanged)
 * - Trailing slashes removed from pathname
 * - Search params and hash fragments preserved
 * Returns null if URL is invalid
 *
 * @example
 * getFullNormalizedUrl('www.example.com/path/') // Returns 'https://example.com/path'
 * getFullNormalizedUrl('https://www.api.example.com/path?q=1#hash') // Returns 'https://www.api.example.com/path?q=1#hash'
 * getFullNormalizedUrl('invalid-url') // Returns null
 */
export function getFullNormalizedUrl(url: string): string | null {
  if (!url) return null;
  try {
    const parsedUrl = new URL(url);

    let hostname = parsedUrl.hostname;

    // Remove www. prefix if it's a subdomain
    if (hostname.startsWith("www.") && hostname.split(".").length > 2) {
      hostname = hostname.slice(4);
    }

    // Remove trailing slash from pathname
    let pathname = parsedUrl.pathname;
    if (pathname.endsWith("/")) {
      pathname = pathname.slice(0, -1);
    }

    // Return the full normalized URL
    return `${parsedUrl.protocol}//${hostname}${pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    console.error(`Invalid URL: ${url}`);
    return null;
  }
}

/**
 * Gets the base URL (protocol + hostname) from a full URL string.
 * Removes path, query parameters, and hash fragments.
 *
 * @param {string} url - The full URL string
 * @returns {string} The base URL (protocol + hostname)
 *
 * @example
 * getBaseUrl('https://google.com/search?q=test#results') // Returns 'https://google.com'
 * getBaseUrl('http://example.com/path?param=1') // Returns 'http://example.com'
 * getBaseUrl('http://www.example.com/path/?param=1') // Returns 'http://example.com'
 */
export function getBaseUrl(url: string): string {
  try {
    // ## getFullNormalizedUrl() to handle www. prefix and trailing slashes.
    const urlObj = new URL(getFullNormalizedUrl(url) || url);
    return `${urlObj.protocol}//${urlObj.hostname}`;
  } catch {
    return url;
  }
}

/**
 * Extracts a comparable hostname from a URL or a bare domain, so two websites written in
 * different shapes (protocol, `www.`, path, casing) can be compared for equality.
 *
 * @param {string} [url] - The URL or bare domain to normalize
 * @returns {string} The normalized hostname, or an empty string when the value is not domain-like
 * (a plain company name, an empty string, or an unparseable URL).
 *
 * @example
 * getNormalizedHostname('https://www.Example.com/about?q=1') // Returns 'example.com'
 * getNormalizedHostname('example.com') // Returns 'example.com'
 * getNormalizedHostname('Acme Inc') // Returns ''
 */
export function getNormalizedHostname(url?: string | null): string {
  const urlWithProtocol = addUrlProtocol(url ?? "");
  if (!urlWithProtocol) return "";

  try {
    const { hostname } = new URL(urlWithProtocol);
    // Without a dot it's not a domain (e.g. a company name typed in the search box).
    if (!hostname.includes(".")) return "";

    return hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return "";
  }
}

/**
 * This function validates and extracts the name and email of an external attendee from a
 * given input string.
 * @param {string} input - The `validateExternalAttendee` function is designed to validate input
 * strings in the format "Name <email>" or just an email address. It uses a regular expression to match
 * and extract the name and email components from the input string.
 * @returns The `validateExternalAttendee` function returns an object with either a `fullName` and
 * `email` property if the input matches the format "Name <email>", or just an `email` property if the
 * input is a valid email string. If the input does not match the expected formats, an empty object is
 * returned.
 */
export function validateMultiFormatEmail(input: string) {
  const match = input.match(EMAIL_VALIDATION_REGEX);

  if (match) {
    if (match[1] && match[2]) {
      return { fullName: match[1], email: match[2] };
    } else if (match[3]) {
      return { email: match[3] };
    }
  }

  return null;
}

/**
 * The function `getUserSplitNameFromFullName` takes a full name as input and returns an object with
 * the first name and last name split from the full name.
 * @param {string} [fullName] - The function `getUserSplitNameFromFullName` takes an optional parameter
 * `fullName` of type string. It splits the full name into first name and last name based on a space
 * character. If the full name is provided and not empty, it returns an object with `firstName` and
 * `lastName` properties
 * @returns { firstName, lastName }
 */
export function getUserSplitNameFromFullName(fullName?: string) {
  if (!fullName?.trim().length) {
    return null;
  }

  const nameSplit = fullName.split(" ");
  const firstName = nameSplit[0];
  const lastName = nameSplit.length > 1 ? nameSplit[nameSplit.length - 1] : "";

  return { firstName, lastName };
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Normalizes an API URL into a path suitable for grouping similar requests
 * (e.g. for Bugsnag's `event.groupingHash`).
 *
 * Strips origin and query string, then replaces UUID segments with `{uuid}` so
 * that the same endpoint shape across resources collapses into one group.
 * Falls back to returning the raw input when the value cannot be parsed as a URL.
 *
 * @example
 * normalizeApiPath('https://api.example.com/deals/<uuid>/?x=1') // '/deals/{uuid}/'
 */
export function normalizeApiPath(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    const segments = u.pathname.split("/").map((s) => (UUID_REGEX.test(s) ? "{uuid}" : s));
    return segments.join("/");
  } catch {
    return rawUrl;
  }
}
